import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { StatusPill, PriorityBadge } from "../../components/Pills";
import { toast } from "sonner";
import { Zap, Battery, MapPin, ArrowLeft } from "lucide-react";

export default function StationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [station, setStation] = useState(null);
  const [devices, setDevices] = useState([]);
  const [form, setForm] = useState({ device_id: "", priority: "standard", target_kwh: 1.0 });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/stations/${id}`).then(({ data }) => setStation(data));
    api.get("/devices").then(({ data }) => {
      setDevices(data);
      if (data[0]) setForm((f) => ({ ...f, device_id: data[0].id }));
    });
  }, [id]);

  const startSession = async () => {
    if (!form.device_id) return toast.error("Add a device first");
    setSubmitting(true);
    try {
      const { data } = await api.post("/sessions/start", {
        device_id: form.device_id,
        station_id: id,
        priority: form.priority,
        target_kwh: Number(form.target_kwh),
      });
      toast.success(`Session started · ${data.priority.toUpperCase()}`);
      navigate("/app/consumer/sessions");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start");
    } finally {
      setSubmitting(false);
    }
  };

  if (!station) return <div className="p-8 text-zinc-500 font-mono text-xs">LOADING…</div>;

  const batt = Math.round((station.battery_kwh / station.battery_max_kwh) * 100);

  return (
    <div className="p-6 lg:p-8">
      <button data-testid="back-btn" onClick={() => navigate(-1)} className="bin-label flex items-center gap-1 mb-4 hover:text-cyan-400">
        <ArrowLeft className="w-3 h-3" /> BACK
      </button>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="bin-card p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="bin-label">STATION · {station.id.slice(0, 8)}</div>
                <h1 className="text-3xl font-black tracking-tighter mt-1">{station.name}</h1>
                <div className="text-sm text-zinc-500 mt-1 flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> {station.address}
                </div>
              </div>
              <StatusPill status={station.status} />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
              <Metric label="Battery" value={`${batt}%`} sub={`${station.battery_kwh.toFixed(1)} / ${station.battery_max_kwh} kWh`} color="#4ADE80" />
              <Metric label="Capacity" value={`${station.capacity_kw} kW`} sub={station.power_source.toUpperCase()} color="#00E5FF" />
              <Metric label="Uptime" value={`${station.uptime_pct}%`} sub="30d rolling" color="#FFA800" />
              <Metric label="Active" value={station.active_sessions} sub="sessions" color="#FACC15" />
            </div>
          </div>

          <div className="bin-card p-6">
            <div className="bin-label mb-3">LIVE SESSIONS · {station.active_session_details?.length || 0}</div>
            {(station.active_session_details || []).length === 0 ? (
              <div className="text-sm text-zinc-500">No active sessions on this station.</div>
            ) : (
              <div className="space-y-2">
                {station.active_session_details.map((s) => (
                  <div key={s.id} className="flex items-center justify-between p-3 border border-zinc-800 rounded">
                    <div>
                      <div className="text-sm font-mono">{s.device_name}</div>
                      <div className="text-xs text-zinc-500">{s.user_name}</div>
                    </div>
                    <div className="text-right">
                      <PriorityBadge priority={s.priority} />
                      <div className="text-xs font-mono mt-1">{s.energy_kwh.toFixed(3)} kWh</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Request power panel */}
        <div className="bin-card p-6 h-fit sticky top-4">
          <div className="bin-label mb-1">REQUEST POWER</div>
          <h2 className="text-xl font-bold mb-5">Open Energy Session</h2>

          <label className="bin-label">Device</label>
          <select
            data-testid="session-device"
            value={form.device_id}
            onChange={(e) => setForm({ ...form, device_id: e.target.value })}
            className="w-full mt-2 mb-4 bg-[#18181b] border border-zinc-800 rounded px-3 py-2 text-sm font-mono"
          >
            {devices.length === 0 && <option value="">No devices</option>}
            {devices.map((d) => (
              <option key={d.id} value={d.id}>{d.name} · {d.power_watts}W</option>
            ))}
          </select>

          <label className="bin-label">Priority</label>
          <select
            data-testid="session-priority"
            value={form.priority}
            onChange={(e) => setForm({ ...form, priority: e.target.value })}
            className="w-full mt-2 mb-4 bg-[#18181b] border border-zinc-800 rounded px-3 py-2 text-sm font-mono"
          >
            <option value="standard">Standard</option>
            <option value="premium">Premium</option>
            <option value="background">Background</option>
            <option value="medical">Medical (eligibility required)</option>
            <option value="emergency">Emergency (eligibility required)</option>
          </select>

          <label className="bin-label">Target (kWh)</label>
          <input
            data-testid="session-target"
            type="number"
            step="0.01"
            min="0.01"
            value={form.target_kwh}
            onChange={(e) => setForm({ ...form, target_kwh: e.target.value })}
            className="w-full mt-2 mb-6 bg-[#18181b] border border-zinc-800 rounded px-3 py-2 text-sm font-mono"
          />

          <button
            data-testid="start-session-btn"
            onClick={startSession}
            disabled={submitting || station.status !== "online"}
            className="w-full bin-btn bin-btn-primary inline-flex items-center justify-center gap-2"
          >
            <Zap className="w-4 h-4" />
            {submitting ? "Routing…" : "Begin Session"}
          </button>
          {station.status !== "online" && (
            <div className="mt-3 text-xs text-amber-400 font-mono">Station unavailable ({station.status})</div>
          )}
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, sub, color }) {
  return (
    <div className="border border-zinc-800 rounded p-3">
      <div className="bin-label">{label}</div>
      <div className="text-2xl font-bold font-mono mt-1" style={{ color }}>{value}</div>
      <div className="text-[0.65rem] text-zinc-500 mt-1 font-mono uppercase">{sub}</div>
    </div>
  );
}
